import { Link } from 'react-router-dom';
import { useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useLocale } from '@/contexts/LocaleContext';
import { optimizedImageUrl } from '@/lib/storage';

const PLACES = [
  {
    value: 'diwaniya',
    ar: 'ديوانية',
    en: 'Diwaniya',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800',
  },
  {
    value: 'home',
    ar: 'بيت',
    en: 'Home',
    image: 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?w=800',
  },
  {
    value: 'chalet',
    ar: 'شالية',
    en: 'Chalet',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=800',
  },
  {
    value: 'school',
    ar: 'مدرسة',
    en: 'School',
    image: 'https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=800',
  },
  {
    value: 'apartment',
    ar: 'شقة',
    en: 'Apartment',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
  },
  {
    value: 'farm',
    ar: 'مزرعة',
    en: 'Farm',
    image: 'https://images.unsplash.com/photo-1500595046743-cd271d694d30?w=800',
  },
  {
    value: 'work',
    ar: 'مكتب',
    en: 'Office',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800',
  },
  {
    value: 'hotel',
    ar: 'فندق',
    en: 'Hotel',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
  },
];

export function PlaceTypesSection() {
  const { locale } = useLocale();
  const isArabic = locale === 'ar';
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: 'prev' | 'next') => {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    const sign = dir === 'next' ? 1 : -1;
    el.scrollBy({ left: isArabic ? -sign * amount : sign * amount, behavior: 'smooth' });
  };

  return (
    <section id="perfect-place" className="py-6 md:py-8 scroll-mt-24">
      <div className="container mx-auto px-4">
        <div className="flex items-end justify-between mb-3 md:mb-4">
          <div>
            <h2 className="text-lg md:text-2xl font-bold">
              {isArabic ? 'مكانك المثالي' : 'Your Perfect Place'}
            </h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1">
              {isArabic ? 'اختر النشاط حسب المكان المناسب' : 'Pick activities by venue'}
            </p>
          </div>
        </div>

        <div className="relative">
          <button
            type="button"
            aria-label={isArabic ? 'السابق' : 'Previous'}
            onClick={() => scroll('prev')}
            className="hidden md:flex absolute start-0 top-[40%] -translate-y-1/2 -translate-x-1/2 rtl:translate-x-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/90 backdrop-blur border border-border shadow-md"
          >
            <ChevronLeft className="h-5 w-5 rtl:hidden" />
            <ChevronRight className="h-5 w-5 hidden rtl:block" />
          </button>
          <button
            type="button"
            aria-label={isArabic ? 'التالي' : 'Next'}
            onClick={() => scroll('next')}
            className="hidden md:flex absolute end-0 top-[40%] -translate-y-1/2 translate-x-1/2 rtl:-translate-x-1/2 z-10 h-10 w-10 items-center justify-center rounded-full bg-background/90 backdrop-blur border border-border shadow-md"
          >
            <ChevronRight className="h-5 w-5 rtl:hidden" />
            <ChevronLeft className="h-5 w-5 hidden rtl:block" />
          </button>

          <div
            ref={scrollRef}
            className="-mx-4 ps-4 pe-4 overflow-x-auto snap-x scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="grid grid-flow-col auto-cols-[60%] sm:auto-cols-[40%] md:auto-cols-[calc((100%-4.5rem)/4)] gap-3 md:gap-5">
              {PLACES.map((p, i) => {
                const name = isArabic ? p.ar : p.en;
                return (
                  <Link
                    key={p.value}
                    to={`/activities?placeType=${p.value}`}
                    className="animate-fade-in snap-start group block"
                    style={{ animationDelay: `${i * 60}ms` }}
                  >
                    <div className="aspect-[4/5] overflow-hidden relative rounded-2xl bg-muted">
                      <img
                        src={optimizedImageUrl(p.image, { width: 500, quality: 70 })}
                        alt={name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                      <div className="absolute bottom-3 start-3 end-3">
                        <h3 className="text-white text-base md:text-lg font-extrabold drop-shadow-md">
                          {name}
                        </h3>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
